import { useCallback, useEffect, useMemo, useRef } from 'react'

const PAUSE_MS = 3000

/**
 * Stop an in-flight search-text typewriter (V1 `SearchLayout.stopAnimationAndClearInput`).
 *
 * @param {{ isAnimatingRef?: { current: boolean }, intervalRef?: { current: ReturnType<typeof setInterval> | null }, timeoutRef?: { current: ReturnType<typeof setTimeout> | null } }} refs
 * @param {React.RefObject<HTMLInputElement | null>} [inputRef]
 */
export function stopSearchTextAnimation(refs, inputRef) {
  if (refs?.isAnimatingRef) {
    refs.isAnimatingRef.current = false
  }
  if (refs?.intervalRef?.current) {
    clearInterval(refs.intervalRef.current)
    refs.intervalRef.current = null
  }
  if (refs?.timeoutRef?.current) {
    clearTimeout(refs.timeoutRef.current)
    refs.timeoutRef.current = null
  }
  if (inputRef?.current) {
    inputRef.current.value = ''
  }
}

/**
 * V1 parity typewriter for AI Display search boxes — writes directly to
 * `inputRef.current.value` to avoid re-render churn while animating.
 */
export function useSearchTextTypewriter({
  searchTexts,
  staticPlaceholder,
  enabled,
  inputRef,
}) {
  const isAnimatingRef = useRef(false)
  const intervalRef = useRef(null)
  const timeoutRef = useRef(null)

  const texts = useMemo(
    () =>
      (Array.isArray(searchTexts) ? searchTexts : [])
        .map((t) => String(t ?? '').trim())
        .filter(Boolean),
    [searchTexts],
  )

  const refs = useMemo(
    () => ({ isAnimatingRef, intervalRef, timeoutRef }),
    [],
  )

  const clearTimers = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const stopAnimation = useCallback(() => {
    stopSearchTextAnimation(refs, inputRef)
  }, [refs, inputRef])

  const applyStaticPlaceholder = useCallback(() => {
    const el = inputRef.current
    if (!el) return
    el.value = ''
    el.placeholder = texts.length === 1 ? texts[0] : staticPlaceholder || ''
  }, [inputRef, staticPlaceholder, texts])

  useEffect(() => {
    clearTimers()
    isAnimatingRef.current = false

    const el = inputRef.current
    if (!el) return

    if (!enabled) {
      el.value = ''
      el.placeholder =
        texts.length === 1 ? texts[0] : staticPlaceholder || ''
      return
    }

    if (texts.length === 0) {
      el.value = ''
      el.placeholder = staticPlaceholder || ''
      return
    }

    if (texts.length === 1) {
      el.value = ''
      el.placeholder = texts[0]
      return
    }

    const startTyping = () => {
      if (!enabled || isAnimatingRef.current) return
      isAnimatingRef.current = true
      if (el) {
        el.placeholder = ''
        el.value = ''
      }

      const animateText = (textIndex) => {
        const text = texts[textIndex] || ''
        const typingSpeed = text.length > 0 ? 1500 / text.length : 100
        let charIndex = 0

        intervalRef.current = setInterval(() => {
          if (!isAnimatingRef.current || !enabled) {
            clearTimers()
            return
          }

          if (charIndex <= text.length) {
            const slice = text.slice(0, charIndex)
            if (inputRef.current && isAnimatingRef.current) {
              inputRef.current.value = slice
            }
            charIndex++
          } else {
            clearTimers()

            timeoutRef.current = setTimeout(() => {
              if (!isAnimatingRef.current || !enabled) return
              if (inputRef.current) {
                inputRef.current.value = ''
              }

              const nextIndex = (textIndex + 1) % texts.length

              timeoutRef.current = setTimeout(() => {
                if (!isAnimatingRef.current || !enabled) return

                if (nextIndex === 0) {
                  if (inputRef.current) {
                    inputRef.current.placeholder = staticPlaceholder || ''
                  }
                  isAnimatingRef.current = false

                  timeoutRef.current = setTimeout(() => {
                    if (enabled) {
                      startTyping()
                    }
                  }, PAUSE_MS)
                } else {
                  animateText(nextIndex)
                }
              }, PAUSE_MS)
            }, PAUSE_MS)
          }
        }, typingSpeed)
      }

      animateText(0)
    }

    startTyping()

    return () => {
      clearTimers()
      isAnimatingRef.current = false
    }
  }, [
    clearTimers,
    enabled,
    inputRef,
    staticPlaceholder,
    texts,
  ])

  return {
    searchTexts: texts,
    stopAnimation,
    applyStaticPlaceholder,
    refs,
    shouldAnimate: texts.length >= 2,
    displayPlaceholder:
      texts.length === 1 ? texts[0] : staticPlaceholder || '',
  }
}
